import { Link } from "wouter";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatCurrency } from "@/lib/formatting";
import { Receipt, Banknote, FileText, ExternalLink } from "lucide-react";
import type { Cost, Revenue, CostMpkAllocation } from "@shared/schema";
import {
  getCostsForProject,
  getRevenuesForProject,
  getCostStatus,
  getRevenueStatus,
} from "./invoiceUtils";

interface InvoiceSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  projectNr: string;
  projectName: string | null;
  panelType: "costs" | "revenues";
  costs: Cost[];
  revenues: Revenue[];
  mpkAllocations: CostMpkAllocation[];
}

export function InvoiceSidePanel({
  isOpen,
  onClose,
  projectNr,
  projectName,
  panelType,
  costs,
  revenues,
  mpkAllocations,
}: InvoiceSidePanelProps) {
  const projectCosts = getCostsForProject(projectNr, costs, mpkAllocations);
  const projectRevenues = getRevenuesForProject(projectNr, revenues);

  const isCosts = panelType === "costs";
  const items = isCosts ? projectCosts : projectRevenues;
  const Icon = isCosts ? Receipt : Banknote;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {isCosts ? "Faktury kosztowe" : "Faktury przychodowe"}
          </SheetTitle>
          <SheetDescription>
            Projekt {projectNr}{projectName ? `: ${projectName}` : ""}
            <span className="ml-2 text-foreground font-medium">
              ({items.length} {items.length === 1 ? "faktura" : items.length < 5 ? "faktury" : "faktur"})
            </span>
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-140px)] mt-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p>Brak faktur dla tego projektu</p>
            </div>
          ) : (
            <div className="space-y-3 pr-4">
              {isCosts
                ? projectCosts.map((cost) => (
                    <CostInvoiceItem key={cost.id} cost={cost} />
                  ))
                : projectRevenues.map((revenue) => (
                    <RevenueInvoiceItem key={revenue.id} revenue={revenue} />
                  ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function CostInvoiceItem({ cost }: { cost: Cost }) {
  const status = getCostStatus(cost);

  return (
    <Link
      href={`/costs?id=${cost.id}`}
      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 hover:border-primary/30 transition-colors cursor-pointer group"
    >
      <div className="space-y-1.5 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate">{cost.nrVat || "-"}</p>
          {cost.rodzaj && (
            <Badge variant="outline" className="text-xs shrink-0">
              {cost.rodzaj}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {cost.nazwaDostawcy || "Brak dostawcy"}
        </p>
        <p className="text-lg font-semibold">
          {formatCurrency(cost.brutto || "0")}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        <CostStatusBadge status={status} />
        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </Link>
  );
}

function RevenueInvoiceItem({ revenue }: { revenue: Revenue }) {
  const status = getRevenueStatus(revenue);

  return (
    <Link
      href={`/revenues?id=${revenue.id}`}
      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 hover:border-primary/30 transition-colors cursor-pointer group"
    >
      <div className="space-y-1">
        <p className="font-medium text-sm">{revenue.nrFvat || "-"}</p>
        <p className="text-lg font-semibold">
          {formatCurrency(revenue.kwotaBrutto || "0")}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <RevenueStatusBadge status={status} />
        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </Link>
  );
}

function CostStatusBadge({ status }: { status: "paid" | "overdue" | "pending" }) {
  switch (status) {
    case "paid":
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Zapłacono
        </Badge>
      );
    case "overdue":
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          Przeterminowana
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          Oczekuje
        </Badge>
      );
  }
}

function RevenueStatusBadge({ status }: { status: "paid" | "unpaid" }) {
  switch (status) {
    case "paid":
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Zapłacona
        </Badge>
      );
    case "unpaid":
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          Niezapłacona
        </Badge>
      );
  }
}
